import { BaseResponse } from "../../../shared/types";

export type InvestmentApplicationStatus =
  | "draft"
  | "pendingReview"
  | "approved"
  | "rejected"
  | "needsRevision";

export type InvestmentProjectStatus = "active" | "archived";

export type InvestmentArchiveReason = "completed" | "removedByAuthor";

export type InvestmentSort = "created" | "published" | "title" | "likes";

export type InvestmentOrder = "asc" | "desc";

export interface InvestmentAuthor {
  userId: string;
  firstName: string;
  lastName: string;
  caliber: string;
  avatarUrl?: string;
}

export interface InvestmentApplicationListItem {
  id: number;
  title: string;
  status: InvestmentApplicationStatus;
  createdAt: string;
  updatedAt: string;
  logoUrl?: string | null;
  targetInvestmentId?: number | null;
  author: InvestmentAuthor;
}

export interface InvestmentApplication {
  id: number;
  status: InvestmentApplicationStatus;
  author: InvestmentAuthor;
  fullName: string;
  email: string;
  phone: string;
  telegram: string;
  whatsapp: string;
  website: string;
  title: string;
  description: string;
  videoPitchUrl: string;
  pitchDeckFileId?: string | null;
  pitchDeckUrl?: string | null;
  logoFileId?: string | null;
  logoUrl?: string | null;
  targetInvestmentId?: number | null;
  rejectionReason?: string | null;
  reviewedAt?: string | null;
  reviewerAdminUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvestmentProjectListItem {
  id: number;
  title: string;
  description: string;
  logoUrl?: string | null;
  status: InvestmentProjectStatus;
  archiveReason?: InvestmentArchiveReason | null;
  isPublicArchiveVisible: boolean;
  isHidden: boolean;
  isExternal: boolean;
  externalSource?: string | null;
  likeCount: number;
  dislikeCount: number;
  publishedAt?: string | null;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  author?: InvestmentAuthor | null;
}

export interface InvestmentProject extends InvestmentProjectListItem {
  website?: string | null;
  videoPitchUrl?: string | null;
  pitchDeckFileId?: string | null;
  pitchDeckUrl?: string | null;
  logoFileId?: string | null;
  founderFullName?: string | null;
  founderEmail?: string | null;
  founderPhone?: string | null;
  founderTelegram?: string | null;
  founderWhatsapp?: string | null;
  applicationId?: number | null;
}

export interface GetAdminInvestmentApplicationsParams {
  status?: InvestmentApplicationStatus;
  pageToken?: string;
  limit?: number;
}

export interface GetAdminInvestmentsParams {
  status?: InvestmentProjectStatus;
  external?: boolean;
  isHidden?: boolean;
  visibleInPublicArchive?: boolean;
  search?: string;
  sort?: InvestmentSort;
  order?: InvestmentOrder;
  pageToken?: string;
  limit?: number;
}

export interface AdminInvestmentApplicationsResponse extends BaseResponse {
  pageToken?: string;
  applications: InvestmentApplicationListItem[];
}

export interface AdminInvestmentApplicationResponse {
  application: InvestmentApplication;
}

export interface AdminInvestmentsResponse extends BaseResponse {
  pageToken?: string;
  investments: InvestmentProjectListItem[];
}

export interface AdminInvestmentResponse {
  investment: InvestmentProject;
}

export interface RejectionReasonBody {
  rejectionReason: string;
}

export interface ExternalInvestmentBody {
  title: string;
  description: string;
  website?: string;
  videoPitchUrl?: string;
  pitchDeckFileId?: string;
  logoFileId?: string;
  founderFullName?: string;
  founderEmail?: string;
  founderPhone?: string;
  founderTelegram?: string;
  founderWhatsapp?: string;
  externalSource?: string;
}

export interface InvestmentFileUploadResponse {
  fileId: string;
  url: string;
}

export const INVESTMENT_APPLICATION_STATUS_LABELS: Record<
  InvestmentApplicationStatus,
  string
> = {
  draft: "Черновик",
  pendingReview: "На модерации",
  approved: "Одобрена",
  rejected: "Отклонена",
  needsRevision: "На доработке",
};

export const INVESTMENT_PROJECT_STATUS_LABELS: Record<
  InvestmentProjectStatus,
  string
> = {
  active: "Активный",
  archived: "Архив",
};

export const INVESTMENT_ARCHIVE_REASON_LABELS: Record<
  InvestmentArchiveReason,
  string
> = {
  completed: "Исполнено",
  removedByAuthor: "Снято с публикации",
};
